import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import UploadZone from './components/UploadZone';
import ProcessingStatus from './components/ProcessingStatus';
import ThumbnailGrid from './components/ThumbnailGrid';
import QualitySettings from './components/QualitySettings';
import { pdfProcessingService } from '../../services/pdfProcessingService';
import { catalogService } from '../../services/catalogService';
import { storageService } from '../../services/storageService';
import { useAuth } from '../../contexts/AuthContext';

const PDFUpload = () => {
  const navigate = useNavigate();
  const { userProfile, activeCompanyId } = useAuth();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [progress, setProgress] = useState(0);
  const [extractedPages, setExtractedPages] = useState([]);
  const [createdCatalogId, setCreatedCatalogId] = useState(null);
  const [quality, setQuality] = useState('standard');
  const [error, setError] = useState(null);

  const handleFileUpload = async (file) => {
    setUploadedFile(file);
    setError(null);
    setProcessing(true);
    setProgress(0);

    try {
      // Validate company ID exist
      const companyId = activeCompanyId || userProfile?.company_id;
      if (!companyId) {
        throw new Error(t('pdfUpload.errors.profileNotLoaded'));
      }

      // Validate PDF file
      pdfProcessingService?.validatePDFFile(file);

      // Stage 1: Extract metadata
      setProcessingStage(t('pdfUpload.processing.status.readingMetadata'));
      setProgress(10);
      const metadata = await pdfProcessingService?.extractPDFMetadata(file);

      // Stage 2: Upload original PDF to public storage bucket
      setProcessingStage(t('pdfUpload.processing.status.convertingPages'));
      setProgress(20);
      let publicPdfPath = null;
      try {
        const { path } = await storageService?.uploadPublicPdf(file, companyId);
        publicPdfPath = path;
        console.log('[PDF_UPLOAD] PDF stored at:', publicPdfPath);
      } catch (pdfErr) {
        console.warn('[PDF_UPLOAD] Failed to store PDF (non-fatal, download disabled):', pdfErr?.message);
      }

      // Stage 3: Process PDF to images
      setProgress(30);
      const pages = await pdfProcessingService?.processPDFToPages(
        file,
        companyId,
        quality
      );

      // Stage 4: Create catalog in database
      setProcessingStage(t('pdfUpload.processing.status.creatingCatalog'));
      setProgress(70);
      const catalog = await catalogService?.createCatalog({
        companyId,
        title: metadata?.title || file?.name?.replace('.pdf', ''),
        totalPages: pages?.length,
        status: 'draft',
        thumbnail: pages?.[0]?.thumbnailUrl,
        thumbnailAlt: `${metadata?.title || file?.name} cover`,
        ...(publicPdfPath ? { publicPdfPath } : {})
      });

      // Stage 4: Save pages to database
      setProcessingStage(t('pdfUpload.processing.status.savingPages'));
      setProgress(85);
      await catalogService?.createCatalogPages(catalog?.id, pages);

      setExtractedPages(pages);
      setCreatedCatalogId(catalog?.id);
      setProgress(100);
      setProcessingStage(t('pdfUpload.processing.status.complete'));
    } catch (err) {
      console.error('PDF upload error:', err);
      setError(err?.message || t('pdfUpload.errors.defaultError'));
      setExtractedPages([]);
    } finally {
      setProcessing(false);
    }
  };

  const extractPagesFromPDF = async (file) => {
    try {
      const pdfjsLib = await import('pdfjs-dist');
      // Use CDN worker to avoid .mjs MIME type issues on servers without explicit mjs config
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

      const arrayBuffer = await file?.arrayBuffer();
      const pdf = await pdfjsLib?.getDocument({ data: arrayBuffer })?.promise;
      const pages = [];

      for (let i = 1; i <= pdf?.numPages; i++) {
        const page = await pdf?.getPage(i);
        const scale = quality === 'high' ? 2.0 : 1.5;
        const viewport = page?.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas?.getContext('2d');
        canvas.height = viewport?.height;
        canvas.width = viewport?.width;

        await page?.render({
          canvasContext: context,
          viewport: viewport
        })?.promise;

        const imageUrl = canvas?.toDataURL('image/jpeg', quality === 'high' ? 0.95 : 0.85);
        pages?.push({
          pageNumber: i,
          imageUrl,
          width: viewport?.width,
          height: viewport?.height
        });
      }

      return pages;
    } catch (error) {
      console.error('PDF extraction error:', error);
      throw new Error(t('pdfUpload.errors.extractionFailed'));
    }
  };

  const handleContinueToEditor = () => {
    navigate(`/catalog-editor?id=${createdCatalogId}`);
  };

  const handleSaveAsDraft = () => {
    navigate('/dashboard');
  };

  const handleReset = () => {
    setUploadedFile(null);
    setExtractedPages([]);
    setCreatedCatalogId(null);
    setProcessing(false);
    setProgress(0);
    setProcessingStage('');
    setError(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div className={`transition-all duration-250 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                {t('pdfUpload.pageTitle')}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {t('pdfUpload.pageSubtitle')}
              </p>
            </div>
            {extractedPages?.length > 0 && (
              <Button
                variant="outline"
                onClick={handleReset}
                iconName="RotateCcw"
                iconPosition="left"
              >
                {t('pdfUpload.startOver')}
              </Button>
            )}
          </div>

          {/* Main Content */}
          <div className="max-w-5xl mx-auto">
            {!uploadedFile && !extractedPages?.length ? (
              <div className="space-y-6">
                <UploadZone
                  onFileUpload={handleFileUpload}
                  disabled={!(activeCompanyId || userProfile?.company_id)}
                  disabledMessage={!userProfile ? t('pdfUpload.dropzone.disabled.loading') : t('pdfUpload.dropzone.disabled.default')}
                />
                <QualitySettings quality={quality} onQualityChange={setQuality} />
                <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Info" size={20} className="text-accent" />
                    {t('pdfUpload.guidelines.title')}
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5" />
                      <span>{t('pdfUpload.guidelines.maxFileSize')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5" />
                      <span>{t('pdfUpload.guidelines.supportedFormat')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5" />
                      <span>{t('pdfUpload.guidelines.recommendedResolution')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5" />
                      <span>{t('pdfUpload.guidelines.passwordProtected')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Processing Status */}
                {processing && (
                  <ProcessingStatus
                    stage={processingStage}
                    progress={progress}
                    fileName={uploadedFile?.name}
                  />
                )}

                {/* Error Display */}
                {error && (
                  <div className="bg-destructive/10 border border-destructive rounded-lg p-4 flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-destructive mb-1">{t('pdfUpload.errors.title')}</h4>
                      <p className="text-sm text-destructive/90">{error}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      iconName="X"
                    />
                  </div>
                )}

                {/* Thumbnail Grid */}
                {extractedPages?.length > 0 && (
                  <ThumbnailGrid pages={extractedPages} />
                )}

                {/* Action Buttons */}
                {extractedPages?.length > 0 && !processing && (
                  <div className="flex flex-col sm:flex-row gap-3 justify-end">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleSaveAsDraft}
                      iconName="Save"
                      iconPosition="left"
                    >
                      {t('pdfUpload.actions.saveAsDraft')}
                    </Button>
                    <Button
                      variant="default"
                      size="lg"
                      onClick={handleContinueToEditor}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      {t('pdfUpload.actions.continueToEditor')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFUpload;